import {
  Building2,
  MapPin,
  Phone,
  Star,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Facility, FacilityType } from "../../../types/facility.definition";
import { facilityTypeLabel } from "../../../utils/enum-label-utils";
import { StatusBadge } from "../StatusBadge/StatusBadge";

interface FacilityCardProps {
  facility: Facility;
  onClick: (facility: Facility) => void;
  /**
   * MED-1495: a disabled facility has no detail page to open, so its card is not
   * a link. Reactivating it from the card itself is then the only way back —
   * without this the facility would be unreachable from the UI altogether.
   */
  onReactivate?: (facility: Facility) => void;
  isReactivating?: boolean;
}

export const FacilityCard: React.FC<FacilityCardProps> = ({
  facility,
  onClick,
  onReactivate,
  isReactivating = false,
}) => {
  const { t } = useTranslation();
  // MED-1543: a photoUrl that does not resolve left the browser rendering the
  // alt text instead, and the facility's name spilled out of the 128px thumb
  // ("TEST Local QA Clinique"). A photo that fails to load is the same thing
  // to a reader as no photo at all, so it falls back to the placeholder this
  // card already has. Keyed on the url so a corrected one is retried.
  const [brokenPhotoUrl, setBrokenPhotoUrl] = useState<string | null>(null);
  const hasPhoto = !!facility.photoUrl && facility.photoUrl !== brokenPhotoUrl;
  const [showAllServices, setShowAllServices] = useState(false);
  const [showAllSpecialties, setShowAllSpecialties] = useState(false);

  const handleShowMoreServices = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAllServices(!showAllServices);
  };

  const handleShowMoreSpecialties = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAllSpecialties(!showAllSpecialties);
  };

  const getFacilityTypeColor = (type: FacilityType) => {
    switch (type) {
      case FacilityType.HOSPITAL:
        return "bg-red-100 text-red-800";
      case FacilityType.CLINIC:
        return "bg-blue-100 text-blue-800";
      case FacilityType.HEALTH_CENTRE:
        return "bg-green-100 text-green-800";
      case FacilityType.RADIOLOGY_CENTER:
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getFacilityTypeIcon = (type: FacilityType) => {
    switch (type) {
      case FacilityType.HOSPITAL:
        return "🏥";
      case FacilityType.CLINIC:
        return "🏥";
      case FacilityType.HEALTH_CENTRE:
        return "💊";
      case FacilityType.HEALTH_POST:
        return "🧪";
      default:
        return "🏢";
    }
  };

  // MED-1495: the facility DETAIL lookup is `findByIdAndDisabledFalse`, so a
  // disabled facility cannot be opened at all — the card used to be a link to
  // "the requested facility could not be found". It is marked disabled and left
  // unclickable instead, with its own reactivate action.
  const isDisabled = !!facility.disabled;

  return (
    <div
      className={`overflow-hidden h-full rounded-lg border shadow-sm transition-shadow duration-200 ${
        isDisabled
          ? "bg-gray-50 border-gray-400 border-dashed"
          : "bg-white border-gray-200 cursor-pointer hover:shadow-md"
      }`}
      {...(isDisabled
        ? { "aria-disabled": true }
        : { onClick: () => onClick(facility) })}
    >
      <div className="flex h-full">
        {/* Facility Image */}
        <div className="flex flex-shrink-0 justify-center items-center w-32 h-32 bg-gray-100">
          {hasPhoto ? (
            <img
              src={facility.photoUrl}
              alt={facility.name}
              className="object-cover w-full h-full"
              onError={() => setBrokenPhotoUrl(facility.photoUrl ?? null)}
            />
          ) : (
            <div className="flex justify-center items-center w-full h-full bg-gradient-to-br from-blue-50 to-blue-100">
              <Building2 className="w-12 h-12 text-blue-400" />
            </div>
          )}
        </div>

        {/* Facility Info */}
        <div className="flex flex-col flex-1 p-4 min-w-0">
          <div className="flex-1 min-w-0">
            <h3
              className="mb-2 text-lg font-semibold text-gray-900 truncate"
              title={facility.name}
            >
              {facility.name}
            </h3>
            <div className="flex flex-wrap gap-1 items-center mb-3">
              <span
                className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getFacilityTypeColor(
                  facility.facilityType
                )}`}
              >
                <span className="mr-1">
                  {getFacilityTypeIcon(facility.facilityType)}
                </span>
                {facilityTypeLabel(t, facility.facilityType)}
              </span>
              <StatusBadge
                status={t(
                  facility.disabled
                    ? "common.statuses.inactive"
                    : "common.statuses.active"
                )}
              />
              {facility.isPartner && (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  {t("admin.facilities.partner.partner")}
                </span>
              )}
            </div>
          </div>

          {/* Disabled notice — stated in words, not by styling alone, and the
              only route left to reactivation (MED-1495). */}
          {isDisabled && (
            <div className="p-2 mb-2 rounded border border-gray-300 bg-gray-100">
              <p className="text-xs text-gray-700">
                {t("admin.facilities.disabledCard.notice")}
              </p>
              {onReactivate && (
                <button
                  type="button"
                  onClick={() => onReactivate(facility)}
                  disabled={isReactivating}
                  aria-label={t(
                    "admin.facilities.disabledCard.reactivateName",
                    {
                      name: facility.name,
                    }
                  )}
                  className="inline-flex items-center px-2 py-1 mt-2 text-xs font-medium text-white rounded bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-600 disabled:opacity-60"
                >
                  {t("admin.facilities.disabledCard.reactivate")}
                </button>
              )}
            </div>
          )}

          {/* Location */}
          <div className="flex items-center mb-2 min-w-0 text-sm text-gray-600">
            <MapPin className="flex-shrink-0 mr-1 w-4 h-4" />
            <span
              className="truncate"
              title={`${facility.location.address}, ${facility.location.city}`}
            >
              {facility.location.address}, {facility.location.city}
            </span>
          </div>

          {/* Contact */}
          <div className="flex items-center mb-2 min-w-0 text-sm text-gray-600">
            <Phone className="flex-shrink-0 mr-1 w-4 h-4" />
            <span
              className="truncate"
              title={formatPhoneNumberForDisplay(facility.phoneNumber)}
            >
              {formatPhoneNumberForDisplay(facility.phoneNumber)}
            </span>
          </div>

          {/* Rating and Reviews */}
          {(facility.rating != null || !!facility.reviews) && (
            <div className="flex items-center mb-3 text-sm text-gray-600">
              <Star className="flex-shrink-0 mr-1 w-4 h-4 text-yellow-400 fill-current" />
              <span className="font-medium">{facility.rating ?? 0}</span>
              {facility.reviews ? (
                <span className="ml-1">
                  (
                  {t("admin.facilities.reviewCount", {
                    count: facility.reviews,
                  })}
                  )
                </span>
              ) : null}
            </div>
          )}

          {/* Services Preview */}
          {facility.careTypes && facility.careTypes.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {facility.careTypes
                .slice(0, showAllServices ? facility.careTypes.length : 2)
                .map((service, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 text-xs text-gray-700 truncate bg-gray-100 rounded max-w-20"
                    title={service.label}
                  >
                    {service.label?.trim() || service.code}
                  </span>
                ))}
              {facility.careTypes.length > 2 && (
                <button
                  onClick={handleShowMoreServices}
                  className="inline-flex items-center px-2 py-1 text-xs text-blue-600 bg-blue-100 rounded transition-colors hover:bg-blue-200"
                  title={
                    showAllServices
                      ? "Show less"
                      : `Show ${facility.careTypes.length - 2} more`
                  }
                >
                  {showAllServices
                    ? "Show less"
                    : `+${facility.careTypes.length - 2}`}
                </button>
              )}
            </div>
          )}

          {/* Specialties */}
          {facility.specialties && facility.specialties.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-auto">
              {facility.specialties
                .slice(0, showAllSpecialties ? facility.specialties.length : 1)
                .map((specialty, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2 py-1 text-xs text-blue-700 truncate bg-blue-100 rounded max-w-20"
                    title={
                      typeof specialty === "string"
                        ? specialty
                        : specialty.label
                    }
                  >
                    {typeof specialty === "string"
                      ? specialty
                      : specialty.label}
                  </span>
                ))}
              {facility.specialties.length > 1 && (
                <button
                  onClick={handleShowMoreSpecialties}
                  className="inline-flex items-center px-2 py-1 text-xs text-blue-600 bg-blue-100 rounded transition-colors hover:bg-blue-200"
                  title={
                    showAllSpecialties
                      ? "Show less"
                      : `Show ${facility.specialties.length - 1} more`
                  }
                >
                  {showAllSpecialties
                    ? "Show less"
                    : `+${facility.specialties.length - 1}`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
